// Copyright (c) StrangeBee 2024

import MdEditor, { Plugins } from 'react-markdown-editor-lite';
import React, { createContext, ReactElement, useEffect, useRef, useState } from 'react';
import { PluginPreview } from '@Atoms/MarkdownEditor/PluginPreview';
import { PluginReference } from '@Atoms/MarkdownEditor/PluginReference';
import { PluginQuote } from '@Atoms/MarkdownEditor/PluginQuote';

import { classList } from '@Helpers/className';
import { UploadFunc } from 'react-markdown-editor-lite/cjs/share/var';
import { useLocalMessage } from '@Helpers/locale';
import MarkdownIt from 'markdown-it';
import insert from 'markdown-it-ins';
import mila from 'markdown-it-link-attributes';

import 'react-markdown-editor-lite/lib/index.css';

import './THMarkdown.scss';

export const THMarkdownContext = createContext({
    viewMode: false,
    setViewMode: (props: any): any => {}, // eslint-disable-line
});

MdEditor.unuse(Plugins.ModeToggle);
MdEditor.unuse(Plugins.BlockCodeInline);
MdEditor.unuse(Plugins.BlockQuote);
MdEditor.unuse(Plugins.Clear);
MdEditor.use(Plugins.FullScreen, { align: 'left' });
MdEditor.use(PluginPreview);
MdEditor.use(PluginQuote);
MdEditor.use(PluginReference);

const mdParser = new MarkdownIt({
    breaks: true,
    linkify: true,
    typographer: true,
})
    .use(insert)
    .use(mila, {
        attrs: {
            target: '_blank',
            rel: 'noreferrer',
        },
    });

interface THMarkdownProps {
    borderless?: boolean;
    disabled?: boolean;
    sameSize?: boolean;
    className?: string;
    value?: string;
    onChange?: (value: string) => void;
    onBlur?: (event: any) => void;
    onImageUpload?: UploadFunc;
    formEditMode?: boolean;
}

export const THMarkdown = React.memo(
    ({
        borderless = false,
        disabled = false,
        sameSize = false,
        className,
        value,
        onChange,
        onImageUpload,
        formEditMode,
        onBlur,
    }: THMarkdownProps): ReactElement => {
        const i = useLocalMessage();
        const markdownRef = useRef<MdEditor>(null);
        const [focus, setFocus] = useState(false);
        const [viewMode, setViewMode] = useState(borderless || disabled);

        useEffect(() => {
            if (markdownRef.current) {
                const textArea: any = markdownRef.current.getMdElement();

                textArea.onfocus = () => setFocus(true);
                textArea.onfocusout = () => setFocus(false);
            }
        }, []);

        useEffect(() => {
            if (formEditMode === false) {
                setViewMode(true);
            }
        }, [formEditMode]);

        return (
            <THMarkdownContext.Provider value={{ viewMode, setViewMode }}>
                <THMarkdownContext.Consumer>
                    {({ viewMode = false }) => (
                        <div
                            className={classList('th-markdown', className, {
                                'th-markdown__focus': focus,
                                'no-border': borderless,
                                'th-markdown__disabled': disabled,
                                'th-markdown__view-mode': viewMode,
                                'th-markdown__equal-size': sameSize,
                            })}
                        >
                            <MdEditor
                                ref={markdownRef}
                                markdownClass="th-markdown__content-edit"
                                value={value}
                                onChange={({ text }) => onChange?.(text)}
                                onBlur={(event) => onBlur?.(event)}
                                readOnly={disabled}
                                onImageUpload={onImageUpload}
                                config={{
                                    view: {
                                        menu: true,
                                        md: !(disabled || borderless),
                                        html: disabled || borderless,
                                        fullScreen: false,
                                        hideMenu: false,
                                    },
                                    table: {
                                        maxRow: 5,
                                        maxCol: 6,
                                    },
                                    syncScrollMode: ['leftFollowRight', 'rightFollowLeft'],
                                }}
                                renderHTML={(text) => {
                                    // handle TH5-2110: https://strangebee.atlassian.net/browse/TH5-2110
                                    const clickableSourceRefEmail = /<([^>]+)>/g;
                                    const modifiedText = text.replace(clickableSourceRefEmail, (_, email) => {
                                        return email;
                                    });
                                    return mdParser.render(modifiedText || i('common.empty-value'));
                                }}
                                plugins={viewMode ? ['preview', 'reference'] : undefined}
                            />
                        </div>
                    )}
                </THMarkdownContext.Consumer>
            </THMarkdownContext.Provider>
        );
    },
);
